import { useState, useEffect, useCallback, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import DashboardLayout from "../layouts/DashboardLayout";
import SetupProgressCard from "../components/dashboard/SetupProgressCard";
import api from "../api";
import { Link, Navigate } from "react-router-dom";
import { Card } from "../components/ui/CCard";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Calendar, CreditCard } from "lucide-react";
import DueFollowUpsCard from "../components/dashboard/DueFollowUpsCard";
import RecentActivityCard from "../components/dashboard/RecentActivityCard";
import TherapistDashboard from "./dashboard/TherapistDashboard";
import TodayQueue from "./clinical/TodayQueue";
import useFeatures from "../hooks/useFeatures";
import { usePeriodFilter, PeriodPicker, ALL_TIME_OPTION, PERIOD_OPTIONS } from "../components/dashboard/financePeriod";

const SESSION_PERIOD_OPTIONS = [ALL_TIME_OPTION, ...PERIOD_OPTIONS];

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function Dashboard() {
  const features = useFeatures();
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState("Doctor");
  const [sessionCount, setSessionCount] = useState(0);
  const [ratingData, setRatingData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [doctorsList, setDoctorsList] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [branchesList, setBranchesList] = useState([]);
  const [accuracyStats, setAccuracyStats] = useState(null);
  const [loadingAccuracy, setLoadingAccuracy] = useState(false);
  const [personalCredits, setPersonalCredits] = useState(0);
  const [orgCredits, setOrgCredits] = useState(0);
  const [recentSessions, setRecentSessions] = useState([]);

  const [manualReviewStats, setManualReviewStats] = useState(null);

  // Home page shows session data directly, no tabs - Finance moved to its
  // own nav section (see src/pages/finance/FinanceHome.js) and Clinical
  // Skills (competency/case-complexity) is hidden for now.
  const { period: sessionPeriod, setPeriod: setSessionPeriod, customStart: sessionCustomStart,
    setCustomStart: setSessionCustomStart, customEnd: sessionCustomEnd, setCustomEnd: setSessionCustomEnd,
    range: sessionRange } = usePeriodFilter({ period: "all_time" });

  // `loading` gates the entire page's full-screen skeleton - only the very
  // first load should ever show that. fetchDashboardData is re-invoked on
  // every period filter change too (doctor/branch/period), and without
  // this guard it would flip `loading` back to true on each one, flashing
  // the whole page back to the generic skeleton instead of just refreshing
  // the Total Sessions tile.
  const hasLoadedOnceRef = useRef(false);

  const fetchDashboardData = useCallback(async (doctorId = null, startDate = null, endDate = null, branchId = null) => {
    try {
      if (!hasLoadedOnceRef.current) setLoading(true);
      const [sessionRes, ratingRes] = await Promise.all([
        api.getSessionCount(doctorId, startDate, endDate, branchId),
        api.getSelfRatingData(doctorId), // dead code (never rendered) - left all-time deliberately
      ]);

      setSessionCount(sessionRes.data.session_count || 0);
      setRatingData(ratingRes.data.rating_data || []);
    } catch (err) {
      console.error("Dashboard: Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, []);

  const fetchCreditsAndHistory = useCallback(async (doctorId = null) => {
    try {
      const params = doctorId ? { doctor_id: doctorId } : {};

      // Use getProfileDetails instead of fetchcreditofloggedinuser
      const [profileRes, consultationsRes] = await Promise.all([
        api.getProfileDetails(),
        api.getAllConsultations(params)
      ]);

      // Update credits: store both personal and organizational
      setOrgCredits(profileRes.data?.org_credit ?? 0);
      setPersonalCredits(profileRes.data?.personal_credit ?? 0);

      const sortedSessions = (consultationsRes.data?.data || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentSessions(sortedSessions);
    } catch (err) {
      console.error("Dashboard: Error fetching credits/history:", err);
    }
  }, []);

  const fetchAccuracyStats = useCallback(async (doctorId = null) => {
    try {
      setLoadingAccuracy(true);
      const res = await api.getCognitiveAccuracyStats(doctorId);
      setAccuracyStats(res.data);
    } catch (err) {
      console.error("Dashboard: Error fetching accuracy stats:", err);
    } finally {
      setLoadingAccuracy(false);
    }
  }, []);

  const fetchDoctorsList = useCallback(async () => {
    try {
      const res = await api.getDoctorsList();
      // Normalise to {id, name, email} so the dropdown uses the same shape
      setDoctorsList(res.data.doctors || []);
    } catch (err) {
      console.error("Dashboard: Error fetching doctors list:", err);
    }
  }, []);

  const fetchBranchesList = useCallback(async () => {
    try {
      const res = await api.fetchOrgBranches();
      setBranchesList(res.data || []);
    } catch (err) {
      console.error("Dashboard: Error fetching branches:", err);
    }
  }, []);

  const fetchReporteesList = useCallback(async () => {
    try {
      const res = await api.getSupervisorReportees();
      // Shape to match admin doctorsList: {id, name, email}
      setDoctorsList(res.data.map(r => ({ id: r.reportee_id, name: r.name, email: r.email })));
    } catch (err) {
      console.error("Dashboard: Error fetching reportees list:", err);
    }
  }, []);

  const fetchManualReviewStats = useCallback(async (doctorId = null, startDate = null, endDate = null, branchId = null) => {
    try {
      const res = await api.getManualReviewStats(doctorId, startDate, endDate, branchId);
      setManualReviewStats(res.data);
    } catch (err) {
      console.error("Dashboard: Error fetching manual review stats:", err);
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
          const name = decoded.sub?.name || authData.name || "Doctor";

          setUserRole(role);
          setUserName(name);

          const adminRoles = ["admin", "ops", "operations"];
          const isAdminUser = adminRoles.includes(role);
          const isSupervisorUser = role === "supervisor";
          setIsAdmin(isAdminUser);
          setIsSupervisor(isSupervisorUser);

          if (!(role === "doctor" || role === "student" || isAdminUser || isSupervisorUser)) {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error("Dashboard: Error decoding token:", err);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Refetch when doctor/branch/period filter changes
  useEffect(() => {
    if (sessionPeriod === "custom" && (!sessionRange || sessionRange.end < sessionRange.start)) return; // incomplete/invalid custom range, wait for it to be fixed
    if (userRole === "doctor" || userRole === "student" || isAdmin || isSupervisor) {
      const periodStart = sessionRange?.start || null;
      const periodEnd = sessionRange?.end || null;
      // Fire every fetch together rather than awaiting them one at a time -
      // each updates its own independent piece of state (and already
      // catches its own errors internally), so sequencing them serially
      // only meant each card popped in with real data at a different
      // moment, which reads as the whole page "flickering" as it settles.
      // Firing them in parallel means they land together instead.
      const refreshData = async () => {
        try {
          await Promise.all([
            fetchDashboardData(selectedDoctorId, periodStart, periodEnd, selectedBranchId),
            isAdmin ? fetchManualReviewStats(selectedDoctorId, periodStart, periodEnd, selectedBranchId) : Promise.resolve(),
            fetchCreditsAndHistory(selectedDoctorId),
            fetchAccuracyStats(selectedDoctorId),
          ]);
        } catch (e) {
          console.error("Error refreshing dashboard for doctor:", e);
        }
      };
      refreshData();
    }
  }, [selectedDoctorId, selectedBranchId, userRole, isAdmin, isSupervisor, fetchDashboardData, fetchAccuracyStats,
      sessionPeriod, sessionCustomStart, sessionCustomEnd]);

  // Fetch the people list independently so it isn't blocked by other failing calls
  useEffect(() => {
    if (isAdmin) fetchDoctorsList();
  }, [isAdmin, fetchDoctorsList]);

  useEffect(() => {
    if (isAdmin) fetchBranchesList();
  }, [isAdmin, fetchBranchesList]);

  useEffect(() => {
    if (isSupervisor) fetchReporteesList();
  }, [isSupervisor, fetchReporteesList]);

  // --- Theme Color Helper ---
  const getThemeColor = (variableName) => {
    // Variables are set on body via data-theme attribute, so we must read from body
    return getComputedStyle(document.body).getPropertyValue(variableName).trim();
  };

  // Helper to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
      r = "0x" + hex[1] + hex[1];
      g = "0x" + hex[2] + hex[2];
      b = "0x" + hex[3] + hex[3];
    } else if (hex.length === 7) {
      r = "0x" + hex[1] + hex[2];
      g = "0x" + hex[3] + hex[4];
      b = "0x" + hex[5] + hex[6];
    }
    return `rgba(${+r}, ${+g}, ${+b}, ${alpha})`;
  }

  // State to hold current theme colors
  const [themeColors, setThemeColors] = useState({
    primary: '#6366f1', // default indigo
    secondary: '#64748b' // default slate
  });

  // Update colors when theme changes (using MutationObserver or just useEffect dependency)
  // Since we don't have direct access to theme context here easily without importing it,
  // we can rely on a re-render or poll. Ideally we use the context.
  // BUT: Dashboard doesn't consume ThemeContext directly yet. Let's assume standard React re-render.
  // Better: Use a simple effect that runs on mount and we can add a listener or just rely on parent updates.
  // Actually, we should use hierarchical theme context if available.
  // Let's use a simpler approach: calculate these inside the chart data creation or use a generic Effect.

  useEffect(() => {
    const updateColors = () => {
      const primary = getThemeColor('--color-primary-500') || '#6366f1';
      const secondary = getThemeColor('--color-secondary-500') || '#64748b';
      setThemeColors({ primary, secondary });
    };

    updateColors();
    // Observer to detect attribute changes on body for theme switch
    const observer = new MutationObserver(updateColors);
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });

    return () => observer.disconnect();
  }, []);


  // --- Chart Data Preparation (Improvement, Accuracy, Component, Condition) ---
  const improvementChartData = {
    labels: ratingData.map((item) => item.date ? new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""),
    datasets: [{
      label: "Self Rating",
      data: ratingData.map((item) => item.rating),
      borderColor: themeColors.primary,
      backgroundColor: hexToRgba(themeColors.primary, 0.1),
      tension: 0.4,
      fill: true,
      pointBackgroundColor: "#ffffff",
      pointBorderColor: themeColors.primary,
      pointBorderWidth: 2,
    }],
  };

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: themeColors.secondary,
        bodyColor: '#475569',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.03)', drawBorder: false },
        ticks: { font: { family: 'Inter', size: 10 }, color: themeColors.secondary }
      },
      x: {
        grid: { display: false },
        ticks: { font: { family: 'Inter', size: 10 }, color: themeColors.secondary }
      }
    },
    elements: {
      point: { radius: 3, hoverRadius: 6 },
      line: { borderWidth: 3 }
    }
  };

  const accuracyOverTimeData = {
    labels: accuracyStats?.accuracy_over_time?.map((item) => item.date ? new Date(item.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : []) || [],
    datasets: [{
      label: "Accuracy %",
      data: accuracyStats?.accuracy_over_time?.map((item) => item.accuracy * 100) || [],
      // Use Secondary or a variation for accuracy
      borderColor: themeColors.primary,
      backgroundColor: hexToRgba(themeColors.primary, 0.1),
      tension: 0.4, fill: true,
      pointBackgroundColor: "#ffffff", pointBorderColor: themeColors.primary, pointBorderWidth: 2
    }],
  };

  const componentAccuracyData = (() => {
    if (!accuracyStats?.average_by_component) return { labels: [], datasets: [] };
    const components = Object.entries(accuracyStats.average_by_component).map(([key, value]) => ({
      displayName: (typeof value === 'object' && value?.display_name) ? value.display_name : key.replace(/_/g, ' '),
      accuracy: (typeof value === 'object' && value?.accuracy) ? value.accuracy : value || 0
    }));
    return {
      labels: components.map(c => c.displayName),
      datasets: [{
        label: "Accuracy %",
        data: components.map(c => c.accuracy),
        backgroundColor: hexToRgba(themeColors.primary, 0.6),
        borderRadius: 6,
      }]
    };
  })();

  const conditionAccuracyData = {
    labels: accuracyStats?.accuracy_by_condition ? Object.keys(accuracyStats.accuracy_by_condition) : [],
    datasets: [{
      label: "Accuracy %",
      data: accuracyStats?.accuracy_by_condition ? Object.values(accuracyStats.accuracy_by_condition) : [],
      backgroundColor: hexToRgba(themeColors.primary, 0.6), // Use primary for themed look
      borderRadius: 6,
    }],
  };


  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] animate-pulse">
          <div className="w-12 h-12 bg-primary-200 rounded-full mb-4"></div>
          <div className="h-4 w-32 bg-secondary-200 rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Supervisor has its own dashboard
  if (userRole === "supervisor") {
    return <Navigate to="/supervisor/dashboard" replace />;
  }

  // Fallback for non-dashboard roles
  if (!(userRole === "doctor" || userRole === "student" || isAdmin)) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center animate-fade-in-up">
          <h2 className="text-2xl font-bold text-secondary-800">Welcome to MindConnect</h2>
          <p className="text-secondary-500 mt-2">Please navigate using the sidebar menu.</p>
        </div>
      </DashboardLayout>
    )
  }

  // Doctors get the Today queue while that feature is on, else the Therapist Dashboard
  if (userRole === 'doctor' && features.ready && features.has("today")) {
    return <TodayQueue />;
  }
  if (userRole === 'doctor' && !loading) {
    return <TherapistDashboard />;
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-tour="dashboard">

        {isAdmin && <SetupProgressCard />}

        {/* HERO SECTION */}
        <div className="flex flex-col md:flex-row justify-between items-end gap-4">
          <div className="animate-slide-in-right" style={{ animationDelay: '0.1s' }}>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-secondary-900 leading-tight">
              Welcome back, <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-primary-400">{userName}</span>
            </h1>
            <p className="text-secondary-500 mt-2 text-lg">Here's your clinical performance overview.</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {isAdmin && branchesList.length > 0 && (
              <div className="w-full md:w-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <select
                  id="branch-filter"
                  className="w-full md:w-56 px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all cursor-pointer hover:bg-white/80"
                  onChange={(e) => setSelectedBranchId(e.target.value || null)}
                  value={selectedBranchId || ""}
                >
                  <option value="">All Branches</option>
                  {branchesList.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            )}
            {(isAdmin || isSupervisor) && doctorsList.length > 0 && (
              <div className="w-full md:w-auto animate-fade-in" style={{ animationDelay: '0.3s' }}>
                <select
                  id="doctor-filter"
                  className="w-full md:w-64 px-4 py-2.5 bg-white/50 backdrop-blur-sm border border-secondary-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-all cursor-pointer hover:bg-white/80"
                  onChange={(e) => setSelectedDoctorId(e.target.value || null)}
                  value={selectedDoctorId || ""}
                >
                  <option value="">{isSupervisor ? 'All My Clinicians' : 'All Doctors Overview'}</option>
                  {doctorsList.map(doc => (
                    <option key={doc.id} value={doc.id}>
                      {doc.name}{doc.email ? ` (${doc.email})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <PeriodPicker
          options={SESSION_PERIOD_OPTIONS}
          period={sessionPeriod}
          onPeriodChange={setSessionPeriod}
          customStart={sessionCustomStart}
          onCustomStartChange={setSessionCustomStart}
          customEnd={sessionCustomEnd}
          onCustomEndChange={setSessionCustomEnd}
        />
        {/* STATS OVERVIEW - Bento Row 1 */}
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4 md:gap-6`}>
          <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-default group">
            <div className="flex justify-between items-start">
              <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide">Total Sessions</span>
              <div className="p-2 bg-green-100 text-green-600 rounded-lg group-hover:rotate-12 transition-transform">
                <Calendar size={20} />
              </div>
            </div>
            <div>
              <span className="text-4xl font-heading font-bold text-secondary-900 block">{sessionCount}</span>
            </div>
          </Card>

          {/* Credits Card */}
          <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-default group">
            <div className="flex justify-between items-start">
              <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide">Available AI Credits</span>
              <div className="p-2 bg-amber-100 text-amber-600 rounded-lg group-hover:rotate-12 transition-transform">
                <CreditCard size={20} />
              </div>
            </div>
            <div className="flex items-baseline gap-2">
              {isAdmin && orgCredits > 0 ? (
                <>
                  <span className="text-4xl font-heading font-bold text-secondary-900 block">{orgCredits}</span>
                  <div className="flex flex-col">
                    <span className="text-[10px] leading-tight text-secondary-400 uppercase font-medium">Org Credits</span>
                    <span className="text-xs text-secondary-500 font-bold">{personalCredits} personal</span>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-4xl font-heading font-bold text-secondary-900 block">{personalCredits}</span>
                  <span className="text-xs text-secondary-400">credits</span>
                </>
              )}
            </div>
          </Card>

          {/* Manual Review Tile - Only for admins */}
          {isAdmin && (
            <Link to="/admin/manual-review" className="block">
              <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.05] active:scale-[0.98] transition-all cursor-pointer group bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-lg">
                <div className="flex justify-between items-start">
                  <span className="text-white/90 font-medium text-sm uppercase tracking-wide">Manual Review</span>
                  <div className="p-2 bg-white/20 rounded-lg group-hover:rotate-12 transition-transform backdrop-blur-sm">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <span className="text-3xl font-bold block">
                    {manualReviewStats ? manualReviewStats.total_flagged : '-'}
                  </span>
                  <span className="text-xs text-white/80">Sessions Need Review</span>
                </div>
              </Card>
            </Link>
          )}


        </div>

        <DueFollowUpsCard doctorId={selectedDoctorId} branchId={selectedBranchId} canDismiss={isAdmin} />

        {/* CHARTS GRID - Bento Row 2 */}
        {false && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Self Rating Trend */}
            <Card className={`p-6 h-[22rem] animate-fade-in-up ${(!accuracyStats || !accuracyStats?.accuracy_over_time || accuracyStats?.accuracy_over_time?.length === 0) ? 'lg:col-span-2' : ''}`} style={{ animationDelay: '0.5s' }}>
              <h3 className="text-lg font-bold text-secondary-800 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-primary-500 rounded-full"></span>
                Session Improvement
              </h3>
              <div className="h-64 w-full">
                {ratingData.length > 0 ? (
                  <Line data={improvementChartData} options={commonChartOptions} />
                ) : (
                  <div className="h-full flex items-center justify-center text-secondary-400 text-sm italic border-2 border-dashed border-secondary-100 rounded-2xl">
                    No rating data available yet.
                  </div>
                )}
              </div>
            </Card>

            {/* Cognitive Accuracy Trend */}
            {accuracyStats && accuracyStats?.accuracy_over_time && accuracyStats?.accuracy_over_time?.length > 0 && (
              <Card className="p-6 h-[22rem] animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                <h3 className="text-lg font-bold text-secondary-800 mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-purple-500 rounded-full"></span>
                  Cognitive Model Accuracy
                </h3>
                <div className="h-64 w-full">
                  <Line data={accuracyOverTimeData} options={commonChartOptions} />
                </div>
              </Card>
            )}
          </div>
        )}

        <RecentActivityCard sessions={recentSessions} onRetried={() => fetchCreditsAndHistory(selectedDoctorId)} />

      </div >
    </DashboardLayout >
  );
}
